  import React, { useEffect, useState, useRef } from "react";
  import { Row, Col, Form, Button, Card, Container, Modal, Spinner } from "react-bootstrap";
  import { jwtDecode } from "jwt-decode";
  import {
    fetchallUsersByPractices,
    generateProgressReport,
    getUserPracticeById,
    fetchPracticeTemplates,
    
  } from "../../api";
  import { ExportReport } from "./exportReport";
  import './patientReports.css';

  export function PatientReports({ patientId }) {
    const [generateReport, setGenerateReport] = useState({
      language: "English",
      includeInitial: false,
      includeFollowUp: false,
      initialDateRange: { from: "", to: "" },
      followUpDateRange: { from: "", to: "" },
      sectionHeadings: "",
      author: "",
      practice: "",
      template: "",
      patient: patientId, // ✅ Add patient ID to the report generation object
      exportedBy: "", // ✅ Add exportedBy to the report generation object
      exportedDate: new Date(), // ✅ Add exportedDate to the report generation object
    });

    const [reportContent, setReportContent] = useState(null); // ✅ Store report as an object
    const [authorData, setAuthorData] = useState([]);
    const [error, setError] = useState(null);
    const [practiceData, setPracticeData] = useState("");
    const [templateData, setTemplateData] = useState("");
    const [sections, setSections] = useState([]);
    const [exporter, setExporter] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);  
    const [loading, setLoading] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
      async function fetchAuthorData() {
        try {
          const token = sessionStorage.getItem("user");
          if (token) {
            const decoded = jwtDecode(token);
            const practiceUsersData = await fetchallUsersByPractices(
              decoded.practice
            );
            setExporter(decoded._id);
            const practitioners = practiceUsersData.filter((user) =>
              user.role.includes("Practitioner")
            );
            setAuthorData(practitioners);
          }
        } catch (error) {
          console.error("Error fetching practitioners:", error);
        }
      }
      fetchAuthorData();
    }, []);

    useEffect(() => {
      if (generateReport.author !== "") {
        async function getPracticeData() {
          try {
            const response = await getUserPracticeById(generateReport.author);
            setPracticeData(response);
          } catch (error) {
            console.error("Error fetching practice data:", error);
          }
        }
        getPracticeData();
      }
    }, [generateReport.author]);

    useEffect(() => {
      if (generateReport.practice !== "") {
        async function getTemplateData() {
          try {
            const templates = await fetchPracticeTemplates(generateReport.author);
            const filteredTemplates = templates.filter(
              (template) =>
                template.templatePracticeId.includes(generateReport.practice) &&
                template.templateInitial === "reportexport"
            );
            setTemplateData(filteredTemplates); // ✅ Set filtered templates
            setGenerateReport((prev) => ({
              ...prev,
              sectionHeadings: "",
              exportedBy: exporter, // ✅ Update exportedBy when practice changes
            }));
          } catch (error) {
            console.error("Error fetching templates:", error);
          }
        }
        getTemplateData();
      }
    }, [generateReport.practice, generateReport.author]); // ✅ Runs when practice/author changes

    useEffect(() => {
      if (generateReport.template !== "") {
        const selectedTemplate = templateData.find(
          (template) => template._id === generateReport.template
        );
    
        if (selectedTemplate) {
          const filteredSections = selectedTemplate.notes.filter(
            (note) => note.sessionsFieldType === "text" || note.sessionsFieldType === "textarea"
          );
          const sectionHeadings = filteredSections.map((note) => note.sessionsSection); // ✅ Keep as array
    
          setSections(selectedTemplate.notes); 
          setGenerateReport((prev) => ({
            ...prev,
            sectionHeadings: sectionHeadings, // ✅ No more `.join()`, it's an array now
          }));
        }
      } 
    }, [generateReport.template, templateData]); // ✅ Runs when template changes
    

    const handleToggle = (field) => {
      setGenerateReport((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleDateChange = (field, type, value) => {
      setGenerateReport((prev) => ({
        ...prev,
        [field]: { ...prev[field], [type]: value },
      }));
    };

    

    const handleConfirmGenerateReport = async () => {
      setShowConfirmation(false); // ✅ Close modal after confirmation
      setLoading(true); // ✅ Start loading animation

      window.scrollTo({ top: 0, behavior: "smooth" });

      const selectedAuthorObj = authorData.find(
        (author) => author.id === generateReport.author
      );
      if (!selectedAuthorObj) {
        alert("Please select an author.");
        return;
      }

      const reportData = {
        author: {
          id: generateReport.author,
          occupation: selectedAuthorObj.occupation,
        },
        language: generateReport.language,
        includeInitial: generateReport.includeInitial,
        includeFollowUp: generateReport.includeFollowUp,
        initialDateRange: generateReport.initialDateRange,
        followUpDateRange: generateReport.followUpDateRange,
        sectionHeadings: generateReport.sectionHeadings,
        patientId: generateReport.patient,
        exportedBy: generateReport.exportedBy, // ✅ Save exportedBy field
      };

      console.log("reportData", reportData);

      try {
        const response = await generateProgressReport(reportData);

        // ✅ Update report content
        setReportContent(response);

        // ✅ Update section headings in state
        setGenerateReport((prev) => ({
          ...prev,
          sectionHeadings: response.sectionHeadings || "",
        }));

        setError(null);
      } catch (error) {
        console.error("Error generating report:", error);
        setError("Failed to generate the report. Please try again.");
      }
      setLoading(false); // ✅ Stop loading animation
    };
  
    const TypingAnimation = () => {
      return (
        <div className="typing-animation">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </div>
      );
    };

    return (
      <div className="p-3">
        <Row>
          {/* ✅ Left Column - Report Generation Fields */}
          <Col md={4}>
            <h5 className="text-primary">Generate Report</h5>
            <p className="text-muted">
              Select options for the report, including date ranges and sections to
              include.
            </p>

            {/* ✅ Author Selection Dropdown */}
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">
                Select Author:
              </Form.Label>
              <Form.Select
                value={generateReport.author}
                onChange={(e) =>
                  setGenerateReport((prev) => ({
                    ...prev,
                    author: e.target.value,
                  }))
                }
              >
                <option value="">-- Select Practitioner --</option>
                {authorData.length > 0 ? (
                  authorData.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.firstName} {author.lastName} ({author.occupation})
                    </option>
                  ))
                ) : (
                  <option disabled>No Practitioners Available</option>
                )}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">
                Select Practice:
              </Form.Label>
              <Form.Select
                value={generateReport.practice}
                onChange={(e) =>
                  setGenerateReport((prev) => ({
                    ...prev,
                    practice: e.target.value,
                  }))
                }
              >
                <option value="">-- Select Practice --</option>
                {practiceData.length > 0 ? (
                  practiceData.map((practice) => (
                    <option
                      key={practice.practiceNumber}
                      value={practice.practiceNumber}
                    >
                      {practice.practiceName}
                    </option>
                  ))
                ) : (
                  <option disabled>No Practices Available</option>
                )}
              </Form.Select>
            </Form.Group>

            {/* ✅ Language Selection */}
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">Language:</Form.Label>
              <div>
                <Form.Check
                  inline
                  label="English"
                  type="radio"
                  name="language"
                  value="English"
                  checked={generateReport.language === "English"}
                  onChange={(e) =>
                    setGenerateReport((prev) => ({
                      ...prev,
                      language: e.target.value,
                    }))
                  }
                />
                <Form.Check
                  inline
                  label="Afrikaans"
                  type="radio"
                  name="language"
                  value="Afrikaans"
                  checked={generateReport.language === "Afrikaans"}
                  onChange={(e) =>
                    setGenerateReport((prev) => ({
                      ...prev,
                      language: e.target.value,
                    }))
                  }
                />
              </div>
            </Form.Group>

            {/* ✅ Initial Assessments Date Range */}
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">
                Include Initial Assessments:
              </Form.Label>
              <Form.Check
                type="switch"
                checked={generateReport.includeInitial}
                onChange={() => handleToggle("includeInitial")}
              />
              {generateReport.includeInitial && (
                <Row className="mt-2">
                  <Col>
                    <Form.Label className="text-primary">From:</Form.Label>
                    <Form.Control
                      type="date"
                      value={generateReport.initialDateRange.from}
                      onChange={(e) =>
                        handleDateChange(
                          "initialDateRange",
                          "from",
                          e.target.value
                        )
                      }
                    />
                  </Col>
                  <Col>
                    <Form.Label className="text-primary">To:</Form.Label>
                    <Form.Control
                      type="date"
                      value={generateReport.initialDateRange.to}
                      onChange={(e) =>
                        handleDateChange("initialDateRange", "to", e.target.value)
                      }
                    />
                  </Col>
                </Row>
              )}
            </Form.Group>

            {/* ✅ Follow-Up Assessments Date Range */}
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">
                Include Follow-Up Assessments:
              </Form.Label>
              <Form.Check
                type="switch"
                checked={generateReport.includeFollowUp}
                onChange={() => handleToggle("includeFollowUp")}
              />
              {generateReport.includeFollowUp && (
                <Row className="mt-2">
                  <Col>
                    <Form.Label className="text-primary">From:</Form.Label>
                    <Form.Control
                      type="date"
                      value={generateReport.followUpDateRange.from}
                      onChange={(e) =>
                        handleDateChange(
                          "followUpDateRange",
                          "from",
                          e.target.value
                        )
                      }
                    />
                  </Col>
                  <Col>
                    <Form.Label className="text-primary">To:</Form.Label>
                    <Form.Control
                      type="date"
                      value={generateReport.followUpDateRange.to}
                      onChange={(e) =>
                        handleDateChange(
                          "followUpDateRange",
                          "to",
                          e.target.value
                        )
                      }
                    />
                  </Col>
                </Row>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">
                Select Report Template:
              </Form.Label>
              <Form.Select
                value={generateReport.template}
                onChange={(e) =>
                  setGenerateReport((prev) => ({
                    ...prev,
                    template: e.target.value,
                  }))
                }
              >
                <option value="">-- Select Template --</option>
                {templateData.length > 0 ? (
                  templateData.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.templateTitle}
                    </option>
                  ))
                ) : (
                  <option disabled>No Templates Available</option>
                )}
              </Form.Select>
            </Form.Group>

            {/* ✅ Section Headings Input */}
            <Form.Group className="mb-3">
              <Form.Label className="text-primary fw-bold">
                Section Headings:
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={generateReport.sectionHeadings}
                readOnly // ✅ Make the field read-only
              />
            </Form.Group>

            {/* ✅ Generate Report Button */}
            <Button
  variant="primary"
  className="w-100 mt-3"
  onClick={() => setShowConfirmation(true)} // ✅ Opens modal
  disabled={loading} // ✅ Disable button when loading
  >
    {loading ? <Spinner animation="border" size="sm" /> : "Generate Report"}
  Generate Report
</Button>
          </Col>

          {/* ✅ Right Column - Generated Report Output */}
          <Col md={8} ref={reportRef}>
          <Container className="border-radius-0 shadow-sm gx-0">
            <h5 className="text-primary">Generated Report</h5>
            {loading ? (
              <Card className="p-3 text-center">
                <TypingAnimation /> {/* ✅ Display animation while generating */}
              </Card>
            ) : reportContent ? (
              <Card className="p-3">
                <pre className="text-muted">{reportContent.generatedText}</pre>
                <ExportReport reportContent={reportContent.generatedText} metaData={generateReport} />
              </Card>
            ) : (
              <p className="text-muted">No report generated yet.</p>
            )}
          </Container>
        </Col>
        </Row>
        <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Report Generation</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to generate this report? A charge will apply at the agreed rate as per your Terms & Conditions.</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmGenerateReport}>
              Generate Report
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
